import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { path } from "./therapyPaths";
import TherapyNav from "./TherapyNav";
import TherapyFooter from "./TherapyFooter";
import { Icon } from "./Icons";
import "./Therapy.css";
import "./TherapyPages.css";

const SERVICES = [
    {
        id: "individual",
        title: "Individual Therapy",
        duration: "50 minutes",
        cadence: "Weekly or biweekly",
        fee: "$140–$200 per session",
        feeNote: "Varies by clinician. See individual therapist profiles for exact fees.",
        format: "In-person (Brooklyn) or telehealth",
        summary:
            "Ongoing therapy for adults working through anxiety, depression, burnout, identity, trauma, or life transitions.",
        detail: [
            "Most clients start weekly. After the first month or two, you and your therapist will figure out what cadence makes sense — some stay weekly, some shift to every other week, some come in more intensively when something acute is happening.",
            "Across our practice, our clinicians draw from CBT, EMDR, IFS, ACT, DBT, psychodynamic, somatic, and mindfulness-based work. Each therapist's primary modalities are listed on their profile, and within sessions, your therapist will talk explicitly about how you're working and adjust as needed.",
            "Our clinicians have specific areas they focus on — anxiety, trauma, ADHD, eating concerns, perinatal mental health, identity work, and more. Use the therapist directory to find someone whose focus matches what's bringing you in.",
        ],
        bestFor: [
            "You're stuck in a pattern and want to understand it",
            "Something has shifted recently — loss, change, transition",
            "You've been in therapy before and want to go deeper",
            "You're looking for someone direct, not just supportive",
        ],
    },
    {
        id: "couples",
        title: "Couples Therapy",
        duration: "60 minutes",
        cadence: "Weekly or biweekly",
        fee: "$185–$225 per session",
        feeNote: "Varies by clinician. Currently offered by 2 of our therapists.",
        format: "In-person (Brooklyn) or telehealth",
        summary:
            "Communication, conflict patterns, repair, intimacy, and the question of what you both want from this relationship.",
        detail: [
            "Our couples therapists are Gottman-trained and pull from emotionally focused therapy. The first 2–3 sessions are an assessment: your therapist will meet with you both together, and then briefly with each of you individually, before developing a treatment plan with you.",
            "We work with couples in any configuration — married, partnered, cohabitating, dating, polyamorous, or in the middle of figuring out what they are. Both pre-marital couples and discernment work for couples deciding whether to stay together are part of what we do.",
            "Couples therapy isn't about deciding who's right. It's about understanding the pattern you keep landing in and figuring out what each person needs to do differently.",
        ],
        bestFor: [
            "The same fight keeps happening",
            "Communication has broken down",
            "Trust has been ruptured and you want to repair it",
            "You're considering separation and want help thinking it through",
        ],
    },
    {
        id: "consultation",
        title: "Free Consultation",
        duration: "20 minutes",
        cadence: "One-time",
        fee: "No charge",
        feeNote: null,
        format: "Phone call",
        summary:
            "A no-pressure call to talk about what you're looking for and figure out which of our clinicians might be the right fit.",
        detail: [
            "Therapy is a relationship, and not every therapist is right for every client. The consultation is how we figure out which of our clinicians is the right person before either party commits to anything.",
            "We'll talk briefly about what's bringing you to therapy, what you've tried before, and what you're hoping to get out of the work. We'll be honest about who on our team would be a good match — and who wouldn't.",
            "If we're not the right practice for you, we'll do our best to point you toward someone who is. Our clinicians keep an active referral list of trusted colleagues across specialties and budgets.",
        ],
        bestFor: [
            "You're new to therapy and don't know where to start",
            "You've worked with someone before and want a different approach",
            "You want to feel out a therapist before booking a full session",
            "You have specific questions about how we work",
        ],
    },
];

function TherapyServices() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <main className="demo demo-therapy">
            <TherapyNav />

            <section className="page-header">
                <div className="demo-container">
                    <p className="demo-eyebrow">Services</p>
                    <h1>What we offer.</h1>
                    <p className="page-lede">
                        Three ways to work with us. All sessions are available
                        in-person at the Brooklyn Heights office or via secure
                        telehealth across New York and New Jersey.
                    </p>
                </div>
            </section>

            {SERVICES.map((service, idx) => (
                <section
                    key={service.id}
                    id={service.id}
                    className={`demo-section ${idx % 2 === 1 ? "demo-alt" : ""}`}
                >
                    <div className="demo-container service-detail">
                        <div className="service-detail-header">
                            <h2>{service.title}</h2>
                            <p className="service-detail-summary">{service.summary}</p>
                        </div>

                        <div className="service-detail-grid">
                            <div className="service-detail-meta">
                                <div className="meta-row">
                                    <Icon name="clock" />
                                    <div>
                                        <span className="meta-label">Duration</span>
                                        <span className="meta-value">{service.duration}</span>
                                    </div>
                                </div>
                                <div className="meta-row">
                                    <Icon name="calendar" />
                                    <div>
                                        <span className="meta-label">Cadence</span>
                                        <span className="meta-value">{service.cadence}</span>
                                    </div>
                                </div>
                                <div className="meta-row">
                                    <Icon name="briefcase" />
                                    <div>
                                        <span className="meta-label">Fee</span>
                                        <span className="meta-value">{service.fee}</span>
                                        {service.feeNote && (
                                            <span className="meta-note">{service.feeNote}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="meta-row">
                                    <Icon name="video" />
                                    <div>
                                        <span className="meta-label">Format</span>
                                        <span className="meta-value">{service.format}</span>
                                    </div>
                                </div>

                                <Link to={path("booking")} className="btn-primary service-cta">
                                    Book {service.title.toLowerCase()}
                                </Link>
                            </div>

                            <div className="service-detail-body">
                                <h3>How it works</h3>
                                {service.detail.map((p, i) => (
                                    <p key={i}>{p}</p>
                                ))}

                                <h3>This might be a fit if</h3>
                                <ul className="service-fit-list">
                                    {service.bestFor.map((item, i) => (
                                        <li key={i}>
                                            <Icon name="check" size={16} />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>
            ))}

            {/* FEES & INSURANCE */}
            <section className="demo-section">
                <div className="demo-container">
                    <div className="section-header">
                        <p className="demo-eyebrow">Fees &amp; insurance</p>
                        <h2>The honest version.</h2>
                    </div>

                    <div className="fees-grid">
                        <div className="fees-card">
                            <h4>Out-of-network</h4>
                            <p>
                                Harbor Therapy is out-of-network. We don't bill
                                insurance directly. If you have out-of-network mental
                                health benefits, we provide a monthly superbill you
                                can submit for partial reimbursement. Many PPO plans
                                reimburse 50–80% after a deductible.
                            </p>
                        </div>
                        <div className="fees-card">
                            <h4>Sliding scale</h4>
                            <p>
                                Most of our clinicians hold a small number of
                                reduced-fee slots for clients experiencing financial
                                hardship. Availability varies by therapist and is
                                often filled, but waitlists are open. Ask during your
                                consultation.
                            </p>
                        </div>
                        <div className="fees-card">
                            <h4>Payment</h4>
                            <p>
                                Payment is due at the time of session. We accept all
                                major credit cards, HSA/FSA cards, and ACH transfers
                                through our secure client portal.
                            </p>
                        </div>
                        <div className="fees-card">
                            <h4>Good Faith Estimate</h4>
                            <p>
                                Per the No Surprises Act, you have a right to receive
                                a Good Faith Estimate of expected charges. We provide
                                this at intake.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="demo-section demo-cta">
                <div className="demo-container">
                    <h2>Pick a starting point.</h2>
                    <p>
                        Not sure which service or which therapist fits? Start with a
                        free consultation — we'll figure it out together.
                    </p>
                    <div className="demo-actions demo-actions-center">
                        <Link to={path("booking")} className="btn-primary">
                            Schedule a consultation
                        </Link>
                        <Link to={path("therapists")} className="btn-secondary">
                            Browse therapists
                        </Link>
                    </div>
                </div>
            </section>

            <TherapyFooter />
        </main>
    );
}

export default TherapyServices;